/**
 * Tracks the last-N products the portal customer opened in the quick-view
 * modal. Persisted in `sessionStorage` so the reel appears the moment the
 * customer navigates back to the catalog home during the same session —
 * closing the tab wipes the list (privacy-friendly, matches "recently
 * viewed" convention on Faire / Alibaba).
 *
 * The stored payload is a compact card projection (id, sku, name, brand,
 * brand_logo_url, image_url, price, price_hidden, stock_status) so the
 * reel renders instantly without a follow-up API call.
 */
import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'uc.portal.recently_viewed';
const CAP = 8;

const read = () => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw).slice(0, CAP) : [];
    } catch {
        return [];
    }
};

const write = (items) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, CAP)));
    } catch { /* quota / private-mode — ignore silently */ }
};

/**
 * Read + write hook. `push(card)` prepends the card to the list (deduped
 * by SKU). Consumers that only need to READ the list should still call
 * this hook so state stays in sync via the storage event.
 */
export function useRecentlyViewed() {
    const [items, setItems] = useState(read);

    // Cross-tab sync (rare — portal is usually single-tab, but harmless).
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setItems(read());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const push = useCallback((card) => {
        if (!card || !card.sku) return;
        setItems((prev) => {
            const dedup = prev.filter((x) => x.sku !== card.sku);
            const next = [card, ...dedup].slice(0, CAP);
            write(next);
            return next;
        });
    }, []);

    return { items, push };
}
